/**
 * Editor de tags com chips inline.
 *
 * Comporta-se como um `<Input>` decorado: o usuario digita texto livre, pressiona
 * uma tecla separadora (`Enter` e `,` por padrao) e a entrada vira um chip removivel.
 * Backspace em campo vazio remove o ultimo chip; o `X` em cada chip remove o
 * proprio.
 *
 * Duplicatas: ignoradas silenciosamente. Se a tag normalizada ja existe em
 * `value`, o input limpa sem mostrar erro nem chamar `onChange` — esse padrao
 * mantem a UX leve quando o usuario cola listas com itens repetidos. Para sinalizar
 * conflito, prefira `validate` retornando uma string de erro.
 */
import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { X } from 'lucide-react';
import { cn } from '@favelafood/shared/lib/cn';

/** Teclas suportadas como separadores. `' '` representa a tecla espaco. */
export type TagSeparator = 'Enter' | ',' | ';' | ' ';

const DEFAULT_SEPARATORS: ReadonlyArray<TagSeparator> = ['Enter', ','];

export interface TagInputProps {
  /** Lista atual de tags (controlled). */
  value: string[];
  /** Callback chamado quando a colecao muda (add/remove). */
  onChange: (tags: string[]) => void;
  /** Placeholder do input quando vazio. */
  placeholder?: string;
  /**
   * Teclas que disparam o commit do texto digitado em chip.
   * Default: `['Enter', ',']`. `' '` (espaco) e suportado.
   */
  separator?: ReadonlyArray<TagSeparator>;
  /**
   * Limite maximo de tags. Ao atingir, o input fica readonly e exibe
   * "Limite de N tags atingido" abaixo do container.
   */
  maxTags?: number;
  /**
   * Validador da tag normalizada. Retornar `false` ou uma `string` exibe erro
   * inline e bloqueia a insercao. Retornar `true` aceita a tag.
   */
  validate?: (tag: string) => boolean | string;
  /**
   * Normalizador opcional aplicado antes de `validate`. Quando ausente,
   * usa `raw.trim()`.
   */
  normalize?: (raw: string) => string;
  /** Quando `true`, desabilita interacao (input + remocao via X). */
  disabled?: boolean;
  /** Classes extra aplicadas ao wrapper externo. */
  className?: string;
}

/** Mapeia separadores nao-Enter para os caracteres correspondentes. */
const SEPARATOR_CHAR: Record<Exclude<TagSeparator, 'Enter'>, string> = {
  ',': ',',
  ';': ';',
  ' ': ' ',
};

/**
 * Decide se o evento de teclado deve disparar commit, baseado nos separadores
 * configurados. Mantemos isolado para reduzir CC do handler principal.
 */
function shouldCommit(
  e: KeyboardEvent<HTMLInputElement>,
  separators: ReadonlyArray<TagSeparator>,
): boolean {
  if (separators.includes('Enter') && e.key === 'Enter') return true;
  for (const sep of separators) {
    if (sep === 'Enter') continue;
    if (e.key === SEPARATOR_CHAR[sep]) return true;
  }
  return false;
}

/**
 * Normaliza o resultado de `validate`: `true` => sem erro, `false` => erro
 * generico, `string` => erro com mensagem custom.
 */
function resolveValidationError(
  result: boolean | string | undefined,
  fallback: string,
): string | null {
  if (result === undefined || result === true) return null;
  if (result === false) return fallback;
  return result;
}

/**
 * `<TagInput>` — entrada de multiplas tags com chips removiveis.
 *
 * Pensado para casos como "tags de produto", "categorias", "rotulos". Para
 * selecao de itens de uma lista pre-existente prefira `<MultiSelect>`.
 */
export function TagInput({
  value,
  onChange,
  placeholder,
  separator = DEFAULT_SEPARATORS,
  maxTags,
  validate,
  normalize,
  disabled = false,
  className,
}: TagInputProps): ReactElement {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const errorId = useId();

  const limitReached = useMemo(
    () => (maxTags !== undefined ? value.length >= maxTags : false),
    [maxTags, value.length],
  );
  const inputDisabled = disabled || limitReached;
  const limitMessage =
    maxTags !== undefined && limitReached ? `Limite de ${maxTags} tags atingido` : null;

  const removeTag = useCallback(
    (index: number) => {
      if (disabled) return;
      const next = value.filter((_, i) => i !== index);
      onChange(next);
      setError(null);
    },
    [disabled, onChange, value],
  );

  const commitDraft = useCallback(() => {
    if (inputDisabled) return;
    const raw = draft;
    if (raw === '') return;
    const normalized = normalize ? normalize(raw) : raw.trim();
    if (normalized === '') {
      setDraft('');
      return;
    }
    if (value.includes(normalized)) {
      // Duplicatas: ignorar silenciosamente — vide JSDoc do componente.
      setDraft('');
      setError(null);
      return;
    }
    const validationError = resolveValidationError(validate?.(normalized), 'Tag inválida');
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    onChange([...value, normalized]);
    setDraft('');
    setError(null);
  }, [draft, inputDisabled, normalize, onChange, validate, value]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && draft === '' && value.length > 0) {
        e.preventDefault();
        removeTag(value.length - 1);
        return;
      }
      if (shouldCommit(e, separator)) {
        e.preventDefault();
        commitDraft();
      }
    },
    [commitDraft, draft, removeTag, separator, value.length],
  );

  const focusInput = useCallback(() => {
    if (!inputDisabled) inputRef.current?.focus();
  }, [inputDisabled]);

  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Clique fora dos chips/X deve focar o input para fluxo continuo.
      if (e.target === e.currentTarget) focusInput();
    },
    [focusInput],
  );

  return (
    <div className={cn('w-full', className)}>
      <div
        role="presentation"
        onMouseDown={handleContainerMouseDown}
        className={cn(
          'flex flex-wrap items-center gap-1.5 rounded-card border border-border ' +
            'bg-bg-input px-3 py-2 transition-colors ' +
            'focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ' +
            'focus-within:ring-offset-bg',
          disabled && 'cursor-not-allowed opacity-60',
          error !== null && 'border-danger',
        )}
      >
        {/* role="list" e explicito por requisito de a11y: Safari/VO ignoram
            <ul> quando o list-style e removido via Tailwind. */}
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ul role="list" className="contents">
          {value.map((tag, index) => (
            <li
              key={`${tag}-${index}`}
              className={cn(
                'inline-flex items-center gap-1 rounded bg-bg-hover px-2 py-0.5 ' +
                  'text-sm text-white',
              )}
            >
              <span className="max-w-[14rem] truncate">{tag}</span>
              <button
                type="button"
                onClick={() => removeTag(index)}
                disabled={disabled}
                aria-label={`Remover ${tag}`}
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded-full text-muted ' +
                    'transition-colors hover:bg-danger/20 hover:text-danger ' +
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger ' +
                    'disabled:cursor-not-allowed disabled:opacity-60',
                )}
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error !== null) setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => commitDraft()}
          placeholder={value.length === 0 ? placeholder : undefined}
          disabled={disabled}
          readOnly={limitReached}
          aria-invalid={error !== null}
          aria-describedby={error !== null ? errorId : undefined}
          className={cn(
            'min-w-[8rem] flex-1 bg-transparent text-sm text-white outline-none ' +
              'placeholder:text-muted disabled:cursor-not-allowed',
          )}
        />
      </div>
      {error !== null && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
      {error === null && limitMessage !== null && (
        <p className="mt-1 text-xs text-muted">{limitMessage}</p>
      )}
    </div>
  );
}
